// =============================================================
// Widgets — site-level components that admins can inject into
// page bodies via a marker element.
//
// Marker element (as stored in the page body):
//
//   <span class="widget-marker" data-widget="contact"></span>
//   <span class="widget-marker" data-widget="agenda"></span>
//
// At render time, `expandWidgets(root)` walks the DOM tree of the
// rendered page and replaces each marker element with the
// corresponding widget's HTML. Unknown kinds are removed silently
// so a typo doesn't break the page.
//
// Why a span (not an HTML comment)? DOMPurify strips HTML comments
// by default, and disabling that for admin-authored content opens a
// subtle XSS surface. A real DOM element with an explicit class is
// auditable, survives sanitization (we list the class in
// ALLOWED_ATTR), and the editor renders it as a clickable chip the
// user can recognize.
// =============================================================

import { contactTpl, agendaTpl } from './templates';
import { sanitizeHtml } from './sanitize';

export type WidgetKind = 'contact' | 'agenda';

/** Render a widget's HTML for the current locale. */
export function renderWidget(kind: string): string {
  switch (kind) {
    case 'contact': return contactTpl();
    case 'agenda':  return agendaTpl();
    default:        return '';
  }
}

/** Human-friendly label for the admin editor's "Insert widget" menu. */
export function widgetLabel(kind: WidgetKind): string {
  switch (kind) {
    case 'contact': return 'Contact form';
    case 'agenda':  return 'Agenda (calendar)';
  }
}

/** List of widget kinds the editor offers. Order = display order. */
export const WIDGET_KINDS: readonly WidgetKind[] = ['contact', 'agenda'];

/**
 * Build the marker element as a string, suitable for embedding in
 * page bodies (in the WYSIWYG source AND the persisted D1 body).
 *
 * The element is empty; the editor will replace it with a richer
 * chip (containing a label) when it mounts, and the renderer will
 * replace it with the full widget HTML at runtime.
 */
export function widgetMarker(kind: WidgetKind): string {
  return `<span class="widget-marker" data-widget="${kind}"></span>`;
}

/**
 * Walk the DOM tree under `root` and replace every `.widget-marker`
 * element with the rendered widget HTML. The marker element itself
 * is removed. Unknown widget kinds are removed silently.
 *
 * The widget HTML is funneled through DOMPurify (the same sanitizer
 * the user body goes through) before insertion, and we use a
 * temporary template element + child moves so we never assign to
 * `outerHTML` directly. `outerHTML = '…'` parses HTML in the same
 * dangerous way `innerHTML` does, so we sidestep both.
 *
 * Display order is the canonical order below — independent of how
 * the markers appear in the source body. The home page has both the
 * contact form and the agenda calendar; we always render the agenda
 * (calendar / events) before the contact form so the marketing
 * rhythm is "see what's coming → reach out". Admins who want a
 * different order can edit their page body to move the markers.
 */
const WIDGET_ORDER: WidgetKind[] = ['agenda', 'contact'];

export function expandWidgets(root: HTMLElement): void {
  const markers = Array.from(root.querySelectorAll('.widget-marker'));
  if (markers.length === 0) return;

  // The first marker is our anchor: its parent + position define where
  // the rendered widgets will be inserted, in canonical order
  // (agenda before contact), independent of source-body marker order.
  const anchor = markers[0];
  const anchorParent = anchor.parentNode;
  if (!anchorParent) return;

  type Slot = { kind: string; html: string };
  const slots: Slot[] = markers.map(m => {
    const kind = m.getAttribute('data-widget') || '';
    const html = renderWidget(kind);
    m.remove();
    return { kind, html };
  });

  // Stable sort by WIDGET_ORDER, keeping relative order within the
  // same kind (so duplicate markers stay where they were).
  const orderIndex = (k: string): number => {
    const i = (WIDGET_ORDER as readonly string[]).indexOf(k);
    return i < 0 ? Number.MAX_SAFE_INTEGER : i;
  };
  slots.sort((a, b) => orderIndex(a.kind) - orderIndex(b.kind));

  // Build a single fragment containing all the rendered widgets in
  // canonical order, then insert it as a block before whatever
  // sibling used to follow the anchor. This keeps any element that
  // originally came after the first marker in the same place.
  const frag = document.createDocumentFragment();
  for (const slot of slots) {
    if (!slot.html) continue;
    const tmpl = document.createElement('template');
    tmpl.innerHTML = sanitizeHtml(slot.html);
    frag.appendChild(tmpl.content);
  }
  // The anchor has been removed above; insert the fragment at the
  // anchor's former position. `insertBefore(node, null)` appends.
  anchorParent.insertBefore(frag, null);
}